

import React, { useState, useEffect } from 'react';
import { Terminal, Cpu, Play, Save, History, Sparkles, MessageSquare, FileQuestion, Loader2, Zap, AlertCircle, CheckCircle, RefreshCcw, Layout, Search, Bot } from 'lucide-react';
import { PromptService, SystemPrompts } from '../../services/promptService';
import { runAiTask } from '../../services/geminiService';

export const PromptConsole: React.FC = () => {
  const [activeTab, setActiveTab] = useState<keyof SystemPrompts>('tutor');
  const [prompts, setPrompts] = useState<SystemPrompts>(PromptService.getPrompts());
  const [instruction, setInstruction] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [isDeployed, setIsDeployed] = useState(false);

  useEffect(() => {
    setInstruction(prompts[activeTab]);
    setTestOutput('');
  }, [activeTab, prompts]);

  const runTest = async () => {
    if (!testInput.trim()) return;
    setIsGenerating(true);
    setTestOutput('');

    try {
      // Fix: runAiTask expects a string as the first argument, not a contents array.
      const response = await runAiTask(
          testInput,
          instruction,
          'gemini-3.5-flash'
      );
      setTestOutput(response.text || "No response received.");
    } catch (error) {
      console.error("Test failed:", error);
      setTestOutput(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeploy = () => {
    PromptService.updatePrompts({ [activeTab]: instruction });
    setPrompts(PromptService.getPrompts());
    setIsDeployed(true);
    setTimeout(() => setIsDeployed(false), 3000);
  };

  const handleReset = () => {
    if (confirm(`Reset ${activeTab} instruction to defaults?`)) {
        PromptService.resetToDefault(activeTab);
        setPrompts(PromptService.getPrompts());
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Terminal className="text-medical-600" /> AI Prompt Lab
          </h2>
          <p className="text-slate-500 font-medium">Engineer specialized personas and generation logic for the platform.</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200 shadow-inner">
          <button 
            onClick={() => setActiveTab('tutor')}
            className={`px-6 py-2.5 rounded-xl text-xs font-black tracking-widest flex items-center gap-2 transition-all ${activeTab === 'tutor' ? 'bg-white text-medical-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <MessageSquare size={16} /> AI Tutor
          </button>
          <button 
            onClick={() => setActiveTab('factory')}
            className={`px-6 py-2.5 rounded-xl text-xs font-black tracking-widest flex items-center gap-2 transition-all ${activeTab === 'factory' ? 'bg-white text-medical-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <FileQuestion size={16} /> Question Factory
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        <div className="xl:col-span-7 flex flex-col space-y-6">
            <div className="bg-slate-900 rounded-[2.5rem] border border-slate-800 overflow-hidden shadow-2xl flex flex-col h-[650px]">
                <div className="bg-slate-800/50 p-6 border-b border-slate-800 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Cpu className="text-medical-400" size={20} />
                        <span className="text-[10px] font-black text-slate-400 tracking-widest">System Instruction Editor</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={handleReset} className="text-[9px] font-black text-slate-500 hover:text-slate-300 tracking-widest flex items-center gap-1.5 transition-colors">
                            <RefreshCcw size={12} /> Reset to Default
                        </button>
                    </div>
                </div>
                
                <div className="flex-1 relative flex">
                    <div className="w-12 h-full bg-slate-800/30 border-r border-slate-800 flex flex-col items-center pt-8 text-[9px] font-mono text-slate-600 space-y-1.5 select-none">
                        {Array.from({length: 40}).map((_, i) => <div key={i}>{(i + 1).toString().padStart(2, '0')}</div>)}
                    </div>
                    <textarea 
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        className="flex-1 bg-transparent p-8 outline-none text-medical-50 text-base font-mono leading-relaxed placeholder:text-slate-700 resize-none caret-medical-500 selection:bg-medical-500/30"
                        spellCheck={false}
                    />
                </div>

                <div className="p-6 bg-slate-800/30 border-t border-slate-800 flex justify-end">
                    <button 
                        onClick={handleDeploy}
                        className={`px-10 py-4 rounded-2xl font-black text-xs tracking-widest flex items-center gap-2 shadow-xl transition-all active:scale-95 ${isDeployed ? 'bg-green-600 text-white shadow-green-900/20' : 'bg-medical-600 text-white hover:bg-medical-500 shadow-medical-900/20'}`}
                    >
                        {isDeployed ? <CheckCircle size={18} /> : <Save size={18} />}
                        {isDeployed ? 'Prompt Deployed' : 'Deploy Draft'}
                    </button>
                </div>
            </div>
        </div>

        <div className="xl:col-span-5 flex flex-col space-y-6">
            <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl overflow-hidden flex flex-col h-[650px]">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Play className="text-slate-400" size={18} />
                        <span className="text-[10px] font-black text-slate-500 tracking-widest">Sandbox Simulation</span>
                    </div>
                </div>

                <div className="p-8 flex-1 flex flex-col space-y-8 overflow-y-auto custom-scrollbar">
                    <div className="space-y-3">
                        <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                            <Search size={14} className="text-medical-500" /> Test Input
                        </label>
                        <div className="relative">
                            <textarea 
                                value={testInput}
                                onChange={(e) => setTestInput(e.target.value)}
                                placeholder="Ask a student question..."
                                className="w-full p-5 bg-slate-50 border border-slate-100 rounded-[1.5rem] outline-none min-h-[140px] resize-none font-medium text-slate-800 placeholder:text-slate-300"
                            />
                            <button 
                                onClick={runTest}
                                disabled={isGenerating || !testInput.trim()}
                                className="absolute bottom-4 right-4 p-4 bg-slate-900 text-white rounded-2xl shadow-xl hover:bg-slate-800 transition-all disabled:opacity-30 active:scale-95"
                            >
                                {isGenerating ? <Loader2 size={20} className="animate-spin" /> : <Play size={20} />}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1 flex flex-col">
                        <div className="flex-1 bg-slate-50 border border-slate-100 rounded-[1.5rem] p-6 font-medium text-slate-800 leading-relaxed relative overflow-hidden overflow-y-auto custom-scrollbar">
                            {testOutput ? (
                                <div className="animate-fade-in text-sm">
                                    <p className="whitespace-pre-wrap">{testOutput}</p>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-slate-300 text-center p-8 opacity-40">
                                    <Sparkles size={48} />
                                    <p className="text-xs font-bold tracking-widest">Initialize a test run via proxy.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
